/**
 * コンテンツメトリクス表示
 *
 * 回答内容（論理性、キーワード適合度、重要発話区間など）の詳細を表示
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Label as LabelIcon,
  Sync as SyncIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

/**
 * メトリクスカード
 */
function MetricCard({ icon, title, score, reasoning }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ ml: 1, flex: 1 }}>
          {title}
        </Typography>
        <Chip
          label={`${score}点`}
          size="small"
          color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error'}
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {reasoning}
      </Typography>
    </Paper>
  );
}

/**
 * ハイライトセグメントの表示
 */
function HighlightSegment({ segment }) {
  const getTagColor = (tag) => {
    switch (tag) {
      case 'strong_answer':
        return 'success';
      case 'weak_answer':
        return 'error';
      case 'important_point':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTagLabel = (tag) => {
    switch (tag) {
      case 'strong_answer':
        return '良い回答';
      case 'weak_answer':
        return '改善点';
      case 'important_point':
        return '重要ポイント';
      default:
        return tag;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Chip
          label={getTagLabel(segment.tag)}
          size="small"
          color={getTagColor(segment.tag)}
          sx={{ mr: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {formatTime(segment.start)} - {formatTime(segment.end)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          label={`${segment.score}点`}
          size="small"
          variant="outlined"
        />
      </Box>
      <Typography variant="body2">
        {segment.summary}
      </Typography>
    </Paper>
  );
}

/**
 * コンテンツメトリクス表示コンポーネント
 */
export default function ContentMetrics({ contentScores, compositeScore, languageAnalysis }) {
  if (!contentScores) {
    return (
      <Typography color="text.secondary">
        コンテンツスコアのデータがありません
      </Typography>
    );
  }

  const metrics = [
    {
      icon: <PsychologyIcon color="primary" />,
      title: '論理的一貫性',
      data: contentScores.logical_consistency
    },
    {
      icon: <LabelIcon color="primary" />,
      title: 'キーワード適合度',
      data: contentScores.keyword_relevance
    },
    {
      icon: <SyncIcon color="primary" />,
      title: '韻律同調',
      data: contentScores.prosody_sync
    }
  ];

  const languageMetrics = languageAnalysis?.metrics || {};
  const highlights = languageAnalysis?.highlight_segments || [];
  const strengths = languageAnalysis?.strengths || [];
  const improvements = languageAnalysis?.improvements || [];

  return (
    <Box>
      {/* 総合スコア */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          コンテンツスコア: {compositeScore}点
        </Typography>
        <Typography variant="body2" color="text.secondary">
          回答内容の総合評価
        </Typography>
      </Box>

      {/* 各メトリクス */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <MetricCard
              icon={metric.icon}
              title={metric.title}
              score={metric.data?.score || 0}
              reasoning={metric.data?.reasoning || '評価データなし'}
            />
          </Grid>
        ))}
      </Grid>

      {/* 強み・改善点 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 強み */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUpIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                強み
              </Typography>
            </Box>
            {strengths.length > 0 ? (
              <List dense>
                {strengths.map((strength, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <StarIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={strength} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                データなし
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* 改善点 */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingDownIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                改善点
              </Typography>
            </Box>
            {improvements.length > 0 ? (
              <List dense>
                {improvements.map((improvement, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={improvement}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                データなし
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 重要発話区間 */}
      {highlights.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              重要発話区間 ({highlights.length}件)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {highlights.map((segment, index) => (
              <HighlightSegment key={index} segment={segment} />
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* 言語メトリクスの詳細 */}
      {languageMetrics && Object.keys(languageMetrics).length > 0 && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              言語解析の詳細
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  PREP構造スコア
                </Typography>
                <Typography variant="h6">
                  {languageMetrics.prep_structure_score || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  明確さ
                </Typography>
                <Typography variant="h6">
                  {languageMetrics.clarity_score || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  簡潔さ
                </Typography>
                <Typography variant="h6">
                  {languageMetrics.conciseness_score || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  自己修正回数
                </Typography>
                <Typography variant="h6">
                  {languageMetrics.self_correction_count || 0}回
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  フィラー影響度
                </Typography>
                <Typography variant="h6">
                  {languageMetrics.filler_impact_score || '-'}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
